import {action, computed, makeObservable, runInAction} from 'mobx';

import type {CachedMediaFactory} from '../../../core/models/CachedMedia/CachedMedia.factory';
import type {RemoteFileStorageService} from '../../../core/system/remoteFileStorage/RemoteFileStorage.service';
import type {Nullable} from '../../../utils/utility-types';

import {type CachedMediaModel} from '../../../core/models/CachedMedia/CachedMedia.model';
import {getUuid} from '../../../utils/getUuid';
import {logger} from '../../../utils/logger';
import {type Destructible, FlagManager} from '../../../utils/store';

export abstract class BaseImageModel implements Destructible {
  blurredCover: boolean = false;
  disableCache: boolean = false;
  isValidSrcFlag = new FlagManager();
  isVisibleFlag = new FlagManager();
  protected blurredMedia: CachedMediaModel;
  protected isLoadedFlag = new FlagManager();
  protected prevBlurredSrc?: CachedMediaModel.Src;
  protected prevSrc: Nullable<CachedMediaModel.Src> = null;
  protected srcMedia: CachedMediaModel;
  private readonly remoteFileStorageService: RemoteFileStorageService;

  protected constructor(
    cachedMediaFactory: CachedMediaFactory,
    remoteFileStorageService: RemoteFileStorageService,
  ) {
    this.remoteFileStorageService = remoteFileStorageService;
    this.blurredMedia = cachedMediaFactory.create();
    this.srcMedia = cachedMediaFactory.create();
    makeObservable(this, {
      init: action.bound,
      isLoaded: computed,
      onError: action.bound,
      onLoad: action.bound,
      src: computed,
      srcForBlur: computed,
    });
  }

  async init(props: BaseImageModel.Props) {
    this.disableCache = Boolean(props.disableCache);
    this.blurredCover = Boolean(props.blurredCover);

    const shouldUpdateSrc = this.isDiff(this.prevSrc, props.src);
    const shouldUpdateBlurredSrc = this.isDiff(
      this.prevBlurredSrc,
      props.blurredSrc,
    );

    if (shouldUpdateSrc) {
      await this.updateSrc(props);
    }

    if (shouldUpdateBlurredSrc) {
      await this.updateBlurred(props);
    }
  }

  destruct() {
    this.blurredMedia.destruct();
    this.srcMedia.destruct();
  }

  get isLoaded() {
    return this.isLoadedFlag.value && this.srcMedia.isLoaded;
  }

  get src() {
    return this.isSrcReady ? this.srcMedia.src : undefined;
  }

  get srcForBlur() {
    const src = this.blurredCover ? this.srcMedia.src : this.blurredMedia.src;

    return this.isSrcReady ? src : undefined;
  }

  protected abstract get isSrcReady(): boolean;

  onError(e) {
    logger.error(e);
    this.isValidSrcFlag.setFalse();
  }

  onLoad() {
    this.isLoadedFlag.setTrue();
  }

  toSource(src?: CachedMediaModel.Src) {
    return this.srcMedia.toSource(src);
  }

  private isDiff(
    src1: Nullable<CachedMediaModel.Src> = null,
    src2: Nullable<CachedMediaModel.Src> = null,
  ) {
    const src1Clean = this.remoteFileStorageService.url.getClean(
      src1?.toString() ?? '',
    );
    const src2Clean = this.remoteFileStorageService.url.getClean(
      src2?.toString() ?? '',
    );

    return src1Clean !== src2Clean;
  }

  private async updateBlurred({
    blurredCover,
    blurredSrc,
  }: BaseImageModel.Props) {
    if (blurredSrc) {
      await this.blurredMedia.init({
        disableCache: this.disableCache,
        id: getUuid(),
        src: blurredSrc,
      });
    }

    runInAction(() => {
      this.prevBlurredSrc = blurredSrc;
      this.blurredCover = Boolean(blurredCover);
    });
  }

  private async updateSrc({src}: BaseImageModel.Props) {
    if (src) {
      await this.srcMedia.init({
        disableCache: this.disableCache,
        id: getUuid(),
        src,
      });
    }

    runInAction(() => {
      this.isValidSrcFlag.setValue(Boolean(src));
      this.prevSrc = src;
    });
  }
}

export namespace BaseImageModel {
  /**
   * Properties for initializing the image model
   * @property blurredCover - Whether to use the main image as blur source
   * @property blurredSrc - Optional URL for the blurred version
   * @property disableCache - Whether to bypass the caching mechanism
   * @property src - Main image source URL
   */
  export type Props = {
    blurredCover?: boolean;
    blurredSrc?: CachedMediaModel.Src;
    disableCache?: boolean;
    src: Nullable<CachedMediaModel.Src>;
  };
}
